import { createUuid } from '@notifi/uuid/browser';
import { useEffect, useState } from 'react';
import { useProposalAttachments } from '@/hooks/proposals/useProposalAttachments';
import { useProposalTemplateAttachments } from '@/hooks/proposalTemplates/useProposalTemplateAttachments';
import type { Attachment } from '@/types/attachment';

interface UseProposalAttachmentStateArgs {
  isEditMode: boolean;
  editId?: string;
  templateId?: string;
}

export function useProposalAttachmentState({
  isEditMode,
  editId,
  templateId,
}: UseProposalAttachmentStateArgs) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [excludedTemplateAttachmentIds, setExcludedTemplateAttachmentIds] = useState<string[]>([]);
  const [attachmentsPopulated, setAttachmentsPopulated] = useState(false);
  const [templateAttachmentsPopulated, setTemplateAttachmentsPopulated] = useState(false);
  const [pendingAttachmentId] = useState(() => createUuid());

  const { data: existingAttachments } = useProposalAttachments(editId ?? '');
  const { data: templateAttachments = [] } = useProposalTemplateAttachments(templateId ?? '');

  useEffect(() => {
    if (!isEditMode || !existingAttachments || attachmentsPopulated) return;
    setAttachments(existingAttachments.filter((a) => !a.isInternal));
    setAttachmentsPopulated(true);
  }, [isEditMode, existingAttachments, attachmentsPopulated]);

  useEffect(() => {
    if (!templateId || !templateAttachments.length || templateAttachmentsPopulated || isEditMode)
      return;
    setAttachments(templateAttachments.filter((a) => !a.isInternal));
    setTemplateAttachmentsPopulated(true);
  }, [templateId, templateAttachments, templateAttachmentsPopulated, isEditMode]);

  function handleCustomerAttachmentsChange(next: Attachment[]) {
    const removedIds = attachments
      .filter((a) => !next.find((n) => n.id === a.id))
      .filter((a) => a.entityType !== 'proposal')
      .map((a) => a.id);
    if (removedIds.length > 0) {
      setExcludedTemplateAttachmentIds((prev) => [...prev, ...removedIds]);
    }
    setAttachments(next);
  }

  return {
    attachments,
    setAttachments,
    pendingAttachmentId,
    excludedTemplateAttachmentIds,
    handleCustomerAttachmentsChange,
  };
}
